import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import './QuestionEditor.css';

const clean = html => DOMPurify.sanitize(html, { ADD_TAGS: ['font'], ADD_ATTR: ['color'] });
const textColors = [
  ['기본색', '#334155'], ['검정', '#000000'], ['회색', '#64748b'], ['빨강', '#dc2626'],
  ['주황', '#c2410c'], ['황금색', '#a16207'], ['초록', '#15803d'], ['청록', '#0f766e'],
  ['파랑', '#0000ff'], ['남색', '#4338ca'], ['보라', '#7e22ce'], ['분홍', '#be185d'],
];

export default function QuestionEditor({ question, typeName, onSave, onCancel }) {
  const [title, setTitle] = useState(question.question_text || '');
  const [answer, setAnswer] = useState(question.correct_answer || '');
  const [maximum, setMaximum] = useState(question.max_score ?? 5);
  const [step, setStep] = useState(question.step ?? 1);
  const isStar = Number(question.question_type_id) === 3;
  const [html, setHtml] = useState(() => clean((question.question_explain || '').replace(/\n/g, '<br>')));
  const initialHtml = useRef(html);
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState('');
  const [colorOpen, setColorOpen] = useState(false);
  const [textColor, setTextColor] = useState('#334155');
  const [customColor, setCustomColor] = useState('#334155');
  const [format, setFormat] = useState({});
  const dialogRef = useRef(null);
  const editorRef = useRef(null);
  const titleRef = useRef(null);
  const expandRef = useRef(null);
  const selectionRef = useRef(null);
  const customColorRef = useRef(null);
  const dirty = title !== (question.question_text || '') || html !== initialHtml.current || answer !== (question.correct_answer || '') ||
    (isStar && (Number(maximum) !== Number(question.max_score ?? 5) || Number(step) !== Number(question.step ?? 1)));

  useLayoutEffect(() => {
    editorRef.current.innerHTML = initialHtml.current;
    titleRef.current.focus({ preventScroll: true });
  }, []);

  useLayoutEffect(() => {
    const input = titleRef.current;
    input.style.height = 'auto';
    input.style.height = `${input.scrollHeight + 2}px`;
  }, [title, expanded]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (expanded) {
      dialog.close();
      dialog.showModal();
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      expandRef.current.focus();
      return () => {
        dialog.close();
        dialog.setAttribute('open', '');
        document.body.style.overflow = previous;
      };
    }
  }, [expanded]);

  useEffect(() => {
    const beforeUnload = event => { event.preventDefault(); event.returnValue = ''; };
    if (dirty) window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);

  useEffect(() => {
    const selectionChanged = () => {
      const selection = window.getSelection();
      if (!selection?.rangeCount || !editorRef.current?.contains(selection.anchorNode) || !editorRef.current.contains(selection.focusNode)) return;
      selectionRef.current = selection.getRangeAt(0).cloneRange();
      if (document.queryCommandState) setFormat({ bold: document.queryCommandState('bold'), italic: document.queryCommandState('italic'), insertOrderedList: document.queryCommandState('insertOrderedList') });
    };
    document.addEventListener('selectionchange', selectionChanged);
    return () => document.removeEventListener('selectionchange', selectionChanged);
  }, []);

  const sync = () => { setHtml(editorRef.current.innerHTML); setError(''); };
  const command = (name, value) => {
    editorRef.current.focus({ preventScroll: true });
    if (selectionRef.current && editorRef.current.contains(selectionRef.current.commonAncestorContainer)) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(selectionRef.current);
    }
    document.execCommand(name, false, value);
    sync();
  };
  const save = async () => {
    if (saving || !dirty) return;
    if (!title.trim()) {
      setError('문항 제목을 입력해 주세요.');
      setPreview(false);
      titleRef.current.focus();
      return;
    }
    if (isStar && (!Number.isFinite(Number(maximum)) || Number(maximum) < 1 || Number(maximum) > 10 || !Number.isFinite(Number(step)) || Number(step) < 0.5 || Number(step) > 5)) {
      setError('최대 점수는 1~10, 점수 간격은 0.5~5 사이로 입력해 주세요.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await onSave({ question_text: title.trim(), question_explain: clean(html).trim(), correct_answer: String(answer).trim(),
        ...(isStar ? {max_score: Number(maximum), step: Number(step)} : {}) });
      if (result === false) throw new Error('save_failed');
    } catch {
      setError('저장하지 못했습니다. 작성한 내용은 유지됩니다. 다시 시도해 주세요.');
    } finally {
      setSaving(false);
    }
  };
  const applyColor = color => {
    command('foreColor', color);
    setTextColor(color);
    setCustomColor(color);
    setColorOpen(false);
  };
  const cancel = () => {
    if (!saving && (!dirty || window.confirm('수정한 내용을 저장하지 않고 닫을까요?'))) onCancel();
  };
  const addLink = event => {
    event.preventDefault();
    let url;
    try { url = new URL(linkUrl); } catch { setLinkError('https://로 시작하는 주소를 입력해 주세요.'); return; }
    if (!['https:', 'http:', 'mailto:'].includes(url.protocol)) { setLinkError('웹 주소 또는 이메일 링크만 사용할 수 있습니다.'); return; }
    command('createLink', url.href);
    setLinkOpen(false);
    setLinkUrl('');
    setLinkError('');
  };

  return (
    <dialog open ref={dialogRef} className={`qe ${expanded ? 'qe--expanded' : ''}`}
      aria-label="문항 수정" onCancel={event => { event.preventDefault(); setExpanded(false); }}
      onKeyDown={event => {
        if (event.key === 'Escape' && colorOpen) { event.preventDefault(); event.stopPropagation(); setColorOpen(false); return; }
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && !event.nativeEvent.isComposing) { event.preventDefault(); save(); }
      }}>
      <header className="qe-header">
        <div><span className="qe-type">{typeName}</span><h3>문항 수정</h3></div>
        <button type="button" ref={expandRef} className="qe-button qe-expand" aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>
          <span aria-hidden="true">{expanded ? '↙' : '↗'}</span> {expanded ? '원래 크기로' : '크게 편집'}
        </button>
      </header>
      <div className="qe-main">
        <label className="qe-label" htmlFor={`qe-title-${question.question_id}`}>문항 제목 <span>필수</span></label>
        <textarea id={`qe-title-${question.question_id}`} ref={titleRef} className="qe-title" rows={1} value={title}
          disabled={saving} onChange={event => { setTitle(event.target.value); setError(''); }} placeholder="문항 제목을 입력하세요" />
        <div className="qe-body-heading"><span className="qe-label" id={`qe-body-${question.question_id}`}>문항 본문</span>
          <div className="qe-tabs" aria-label="본문 보기 방식">
            <button type="button" aria-pressed={!preview} onClick={() => setPreview(false)}>편집</button>
            <button type="button" aria-pressed={preview} onClick={() => { setPreview(true); setLinkOpen(false); setColorOpen(false); }}>미리보기</button>
          </div>
        </div>
        <div className="qe-paper">
          {!preview && <div className="qe-toolbar" role="group" aria-label="본문 서식">
            {[['bold', '굵게', <b key="b">B</b>], ['italic', '기울임', <i key="i">I</i>], ['insertOrderedList', '번호 목록', '1. 목록']].map(([name, label, icon]) =>
              <button type="button" key={name} title={label} aria-label={label} aria-pressed={!!format[name]} disabled={saving}
                onMouseDown={event => event.preventDefault()} onClick={() => command(name)}>{icon}</button>)}
            <span className="qe-divider" />
            <button type="button" title="글자색 선택" aria-label="글자색 선택" aria-expanded={colorOpen} aria-controls={`qe-colors-${question.question_id}`}
              disabled={saving} onMouseDown={event => event.preventDefault()} onClick={() => { setColorOpen(value => !value); setLinkOpen(false); }}>
              <span className="qe-color-indicator" style={{textDecorationColor: textColor}} aria-hidden="true">A</span> 글자색 <span aria-hidden="true">⌄</span>
            </button>
            <button type="button" disabled={saving} aria-expanded={linkOpen} onMouseDown={event => event.preventDefault()} onClick={() => { setLinkOpen(value => !value); setColorOpen(false); }}>링크</button>
            <span className="qe-divider" />
            <button type="button" disabled={saving} onMouseDown={event => event.preventDefault()} onClick={() => { command('removeFormat'); command('unlink'); }}>서식 지우기</button>
          </div>}
          {colorOpen && !preview && <div id={`qe-colors-${question.question_id}`} className="qe-colors" role="group" aria-label="글자색 팔레트">
            <div className="qe-color-heading">글자색 <span>문구를 선택한 뒤 색상을 골라 주세요.</span></div>
            <div className="qe-swatches">
              {textColors.map(([name, color]) => <button type="button" key={color} title={name} aria-label={`${name} 글자색`} aria-pressed={textColor === color}
                disabled={saving} style={{'--swatch-color':color}} onMouseDown={event => event.preventDefault()} onClick={() => applyColor(color)}>
                <span aria-hidden="true">{textColor === color ? '✓' : ''}</span>
              </button>)}
            </div>
            <div className="qe-custom-color">
              <label htmlFor={`qe-custom-color-${question.question_id}`}>직접 선택</label>
              <input ref={customColorRef} id={`qe-custom-color-${question.question_id}`} type="color" value={customColor} disabled={saving}
                onInput={event => setCustomColor(event.currentTarget.value)} onChange={event => setCustomColor(event.target.value)} />
              <span>{customColor.toUpperCase()}</span>
              <button type="button" disabled={saving} onMouseDown={event => event.preventDefault()} onClick={() => applyColor(customColorRef.current.value)}>선택 색상 적용</button>
            </div>
          </div>}
          {linkOpen && <form className="qe-link" onSubmit={addLink}>
            <label htmlFor={`qe-link-${question.question_id}`}>링크 주소</label>
            <input autoFocus id={`qe-link-${question.question_id}`} value={linkUrl} placeholder="https://" onChange={event => setLinkUrl(event.target.value)} />
            <button type="submit" disabled={saving}>적용</button><button type="button" onClick={() => setLinkOpen(false)}>닫기</button>
            {linkError && <span role="alert">{linkError}</span>}
          </form>}
          <div ref={editorRef} className="qe-content" role="textbox" aria-label="문항 본문" aria-multiline="true" hidden={preview}
            contentEditable={!saving} suppressContentEditableWarning onInput={sync} data-placeholder="안내문이나 문항 설명을 입력하세요."
            onPaste={event => {
              event.preventDefault();
              const rich = event.clipboardData.getData('text/html');
              if (rich) command('insertHTML', clean(rich));
              else command('insertText', event.clipboardData.getData('text/plain'));
            }} onDrop={event => event.preventDefault()} />
          {preview && <div className="qe-content qe-preview" aria-label="본문 미리보기" dangerouslySetInnerHTML={{ __html: clean(html) || '<span class="qe-empty">입력한 본문이 여기에 표시됩니다.</span>' }} />}
        </div>
        {(question.correct_answer ?? '').toString().trim() !== '' && <label className="qe-answer">정답
          <input value={answer} disabled={saving} onChange={event => setAnswer(event.target.value)} />
        </label>}
        {isStar && <fieldset className="qe-star"><legend>별점 설정</legend>
          <label>최대 점수<input type="number" aria-label="최대 점수" min="1" max="10" value={maximum} disabled={saving} onChange={event => setMaximum(event.target.value)} /></label>
          <label>점수 간격<input type="number" aria-label="점수 간격" min="0.5" max="5" step="0.5" value={step} disabled={saving} onChange={event => setStep(event.target.value)} /></label>
        </fieldset>}
      </div>
      <footer className="qe-footer">
        <div className="qe-status" aria-live="polite">{error ? <span className="qe-error" role="alert">{error}</span> : saving ? '변경사항을 저장하고 있습니다.' : dirty ? <><span className="qe-dot" /> 저장하지 않은 변경사항</> : '내용을 수정한 뒤 저장해 주세요.'}</div>
        <div className="qe-actions"><button type="button" className="qe-button" disabled={saving} onClick={cancel}>취소</button>
          <button type="button" className="qe-button qe-save" disabled={saving || !dirty} onClick={save} title="Ctrl 또는 ⌘ + Enter로 저장">{saving ? '저장 중…' : '변경사항 저장'}</button>
        </div>
      </footer>
    </dialog>
  );
}
